import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Spinner from './ui/Spinner';

// Where a regular (org_user) account lands — the only tenant page that is theirs.
export const USER_HOME = '/imzam';

// Where someone lands after signing in, by role.
export const homePathFor = (role) => {
  if (role === 'super_admin') return '/admin/dashboard';
  if (role === 'org_user') return USER_HOME;
  return '/dashboard';
};

export const ProtectedRoute = ({ children, requireRole = null }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (loading) {
    return <Spinner fullScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role requirement
  if (requireRole && user.role !== requireRole) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

export const SuperAdminRoute = ({ children }) => (
  <ProtectedRoute requireRole="super_admin">{children}</ProtectedRoute>
);

export const OrgAdminRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  // Wait for auth state to resolve before checking role — on a hard
  // navigation (typing the URL directly, not client-side nav), `user` is
  // still null while AuthContext's initial fetchCurrentUser() is in
  // flight. Checking the role before that resolves always fails and
  // falsely bounces the user to /unauthorized.
  if (loading) {
    return <Spinner fullScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Regular users (org_user, typically auto-created by Microsoft SSO) have one
  // page of their own — send them there instead of a dead-end "unauthorized".
  if (user?.role !== 'super_admin' && user?.role !== 'org_admin') {
    return <Navigate to={user?.role === 'org_user' ? USER_HOME : '/unauthorized'} replace />;
  }

  return children;
};
